import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { setCachedOnboardingStatus } from "../../platform/onboardingStatus";
import { getSessionHint } from "../../platform/sessionHint";

function headers() {
  const token = sessionStorage.getItem("syrakis_token");
  return { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const initialState = {
  tenantOwner: { domain: "", rut: "", nombre: "", fantasia: "", giro: "" },
  superadmin: { nombre: "", email: "" },
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    fetch("/api/onboarding", { credentials: "same-origin", headers: headers(), signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || "No se pudo cargar el onboarding.");
        if (!mounted) return;
        if (data.onboarding?.status === "completed") {
          setCachedOnboardingStatus(
            getSessionHint()?.session_key || `legacy:${sessionStorage.getItem("syrakis_token")}`,
            "completed",
          );
          navigate("/dashboard", { replace: true });
          return;
        }
        setForm({ tenantOwner: data.tenantOwner || initialState.tenantOwner, superadmin: data.superadmin || initialState.superadmin });
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.name === "AbortError"
          ? "La consulta de configuración inicial tardó demasiado. Reintenta la carga."
          : (err.message || "No se pudo cargar la configuración inicial."));
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [navigate, loadAttempt]);

  function update(section, field, value) {
    setForm((current) => ({ ...current, [section]: { ...current[section], [field]: value } }));
  }

  async function saveAndContinue(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "PUT", credentials: "same-origin", headers: headers(), body: JSON.stringify(form) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudieron guardar los datos.");
      navigate("/onboarding/mfa", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    setError("");
    if (password.length < 8) {
      setError("La nueva clave debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirmation) {
      setError("Las claves no coinciden.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/onboarding/password", {
        method: "PUT",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo cambiar la clave.");
      setPassword("");
      setPasswordConfirmation("");
      setShowPasswordForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="dashboard-main-body"><div className="card p-24">Cargando configuración inicial...</div></div>;

  return (
    <div className="dashboard-main-body">
      <div className="row justify-content-center">
        <div className="col-xxl-9 col-xl-10">
          <div className="mb-24">
            <span className="text-sm text-primary-600 fw-semibold">Primer acceso · Paso 1 de 2</span>
            <h4 className="mt-8 mb-8">Bienvenido a Sinapso Core</h4>
            <p className="text-secondary-light mb-0">Confirma los datos iniciales del tenant propietario y de tu cuenta de superadministrador. Podrás editarlos antes de comenzar a operar.</p>
          </div>

          {error && <div className="alert alert-danger mb-24">{error}{error.includes("tardó demasiado") && <button type="button" className="btn btn-sm btn-outline-danger ms-3" onClick={() => { setLoading(true); setError(""); setLoadAttempt((attempt) => attempt + 1); }}>Reintentar</button>}</div>}

          <form onSubmit={saveAndContinue}>
            <section className="card p-24 mb-24">
              <div className="d-flex align-items-center gap-3 mb-20"><span className="w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center"><Icon icon="solar:buildings-2-linear" /></span><div><h6 className="mb-4">01 · Tenant Owner</h6><p className="text-sm text-secondary-light mb-0">Empresa propietaria de esta instalación.</p></div></div>
              <div className="row gy-16">
                {[["domain", "Dominio", "text"], ["rut", "RUT", "text"], ["nombre", "Razón social", "text"], ["fantasia", "Nombre de fantasía", "text"], ["giro", "Giro", "text"]].map(([field, label, type]) => <div className={field === "giro" ? "col-12" : "col-md-6"} key={field}><label className="form-label text-sm fw-semibold" htmlFor={`owner-${field}`}>{label}</label><input id={`owner-${field}`} className="form-control radius-8" type={type} value={form.tenantOwner[field] || ""} onChange={(event) => update("tenantOwner", field, event.target.value)} required={field === "domain" || field === "nombre"} /></div>)}
              </div>
            </section>

            <section className="card p-24 mb-24">
              <div className="d-flex align-items-center gap-3 mb-20"><span className="w-40-px h-40-px rounded-circle bg-success-50 text-success-600 d-flex justify-content-center align-items-center"><Icon icon="solar:user-id-linear" /></span><div><h6 className="mb-4">02 · Primer superadmin</h6><p className="text-sm text-secondary-light mb-0">Cuenta que administra la plataforma y completa este onboarding.</p></div></div>
              <div className="row gy-16"><div className="col-md-6"><label className="form-label text-sm fw-semibold" htmlFor="superadmin-name">Nombre</label><input id="superadmin-name" className="form-control radius-8" value={form.superadmin.nombre || ""} onChange={(event) => update("superadmin", "nombre", event.target.value)} required /></div><div className="col-md-6"><label className="form-label text-sm fw-semibold" htmlFor="superadmin-email">Email</label><input id="superadmin-email" type="email" className="form-control radius-8" value={form.superadmin.email || ""} onChange={(event) => update("superadmin", "email", event.target.value)} required /></div></div>
              <div className="mt-20 pt-20 border-top">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowPasswordForm((value) => !value)}>
                  <Icon icon="solar:lock-password-outline" className="me-2" />
                  {showPasswordForm ? "Cancelar cambio de clave" : "Cambiar clave"}
                </button>
                {showPasswordForm && <div className="row gy-16 mt-4">
                  <div className="col-md-6"><label className="form-label text-sm fw-semibold" htmlFor="new-password">Nueva clave</label><div className="position-relative"><input id="new-password" type={showPassword ? "text" : "password"} className="form-control radius-8 pe-48" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} autoComplete="new-password" /><button type="button" className="position-absolute end-0 top-50 translate-middle-y me-12 border-0 bg-transparent text-secondary-light" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar nueva clave" : "Mostrar nueva clave"}><Icon icon={showPassword ? "solar:eye-closed-linear" : "solar:eye-linear"} /></button></div></div>
                  <div className="col-md-6"><label className="form-label text-sm fw-semibold" htmlFor="confirm-password">Repetir clave</label><div className="position-relative"><input id="confirm-password" type={showPasswordConfirmation ? "text" : "password"} className="form-control radius-8 pe-48" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} minLength={8} autoComplete="new-password" /><button type="button" className="position-absolute end-0 top-50 translate-middle-y me-12 border-0 bg-transparent text-secondary-light" onClick={() => setShowPasswordConfirmation((value) => !value)} aria-label={showPasswordConfirmation ? "Ocultar confirmación" : "Mostrar confirmación"}><Icon icon={showPasswordConfirmation ? "solar:eye-closed-linear" : "solar:eye-linear"} /></button></div></div>
                  <div className="col-12"><button type="button" className="btn btn-success btn-sm" onClick={changePassword} disabled={saving}>{saving ? "Guardando..." : "Guardar nueva clave"}</button></div>
                </div>}
              </div>
            </section>

            <div className="d-flex justify-content-end gap-2"><button type="submit" className="btn btn-primary px-24" disabled={saving}>{saving ? "Guardando..." : "Confirmar y entrar al Core"}<Icon icon="solar:arrow-right-linear" className="ms-2" /></button></div>
          </form>
        </div>
      </div>
    </div>
  );
}
